import * as Common from '@piccolohealth/pbs-common';
import { useLocalStorage, useRequiredParams } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import Collaboration from '@tiptap/extension-collaboration';
import CollaborationCursor from '@tiptap/extension-collaboration-cursor';
import React from 'react';
import { PiccoloError } from '../../components/generic/PiccoloError';
import { Editor } from '../../components/tiptap/Editor';
import { useEditor } from '../../components/tiptap/hooks/useEditor';
import { useHocuspocusProvider } from '../../components/tiptap/hooks/useHocuspocusProvider';
import { useImageUpload } from '../../components/tiptap/hooks/useImageUpload';
import { EditorLayout } from '../../components/tiptap/layout/EditorLayout';
import { EditorMenu } from '../../components/tiptap/menus/EditorMenu';
import { getParticipantDocumentExtensions } from '../../components/tiptap/utils';
import { useParticipantDocumentQuery } from '../../graphql/hooks/useParticipantQuery';
import { useAppContext } from '../../hooks/useAppContext';
import { ParticipantDocumentSidebar } from './ParticipantDocumentSidebar';
import { ParticipantDocumentTitleBar } from './ParticipantDocumentTitleBar';

interface ParticipantDocumentEditorContentProps {
  participantDocument: Common.ParticipantDocument;
  isEditable: boolean;
}

export const ParticipantDocumentEditorContent = (props: ParticipantDocumentEditorContentProps) => {
  const { participantDocument, isEditable } = props;

  const { config, organization } = useAppContext();

  const onImageUpload = useImageUpload({
    imageUploadUrl: `${config.api.webUrl}/organizations/${organization.id}/participants/${participantDocument.participantId}/participantDocuments/${participantDocument.id}/images`,
  });

  const documentId = `participantDocument/${participantDocument.id}`;
  const variables = participantDocument.variables;

  const [sidebarOpen, setIsSidebarOpen] = useLocalStorage('editor-sidebar-open', true);
  const isSidebarOpen = sidebarOpen ?? true;

  const toggleSidebarOpen = React.useCallback(() => {
    setIsSidebarOpen(!isSidebarOpen);
  }, [isSidebarOpen, setIsSidebarOpen]);

  const { provider, user, state } = useHocuspocusProvider({
    documentId,
  });

  const document = provider.document;

  const extensions = getParticipantDocumentExtensions({ variables, onImageUpload, document });

  const { editor } = useEditor(
    {
      isEditable,
      extensions: P.compact([
        ...extensions,
        Collaboration.configure({
          document: provider.document,
        }),
        // Read only editors we don't want to have any "awareness" in the backend.
        // I.e. they are viewing this in read only mode.
        isEditable
          ? CollaborationCursor.configure({
              provider,
              user,
            })
          : null,
      ]),
    },
    [isEditable],
  );

  if (!editor) {
    return null;
  }

  return (
    <EditorLayout
      documentId={documentId}
      state={state}
      isSidebarOpen={isSidebarOpen}
      toolbarContent={<EditorMenu editor={editor} />}
      headerContent={
        <ParticipantDocumentTitleBar
          editor={editor}
          participantDocument={participantDocument}
          state={state}
          isSidebarOpen={isSidebarOpen}
          toggleSidebarOpen={toggleSidebarOpen}
        />
      }
      mainContent={<Editor editor={editor} />}
      sidebarContent={
        <ParticipantDocumentSidebar participantDocument={participantDocument} editor={editor} />
      }
    />
  );
};

interface Props {
  participantId: string;
  isDisabled?: boolean;
}

export const ParticipantDocumentEditor = (props: Props) => {
  const { participantId, isDisabled } = props;
  const { documentId } = useRequiredParams<{
    documentId: string;
  }>();

  const { organization } = useAppContext();

  const [, setCurrentDocument] = useLocalStorage(`${participantId}-document`);

  const { isLoading, error, data } = useParticipantDocumentQuery({
    organizationId: organization.id,
    participantId,
    participantDocumentId: documentId,
  });

  const participantDocument = data?.organization?.participant
    ?.document as Common.ParticipantDocument | null;

  React.useEffect(() => {
    if (participantDocument) {
      setCurrentDocument(participantDocument?.id);
    }
  }, [participantDocument, setCurrentDocument]);

  if (isLoading) {
    return null;
  }

  if (error) {
    return <PiccoloError error={error} />;
  }

  if (!participantDocument) {
    return (
      <PiccoloError
        error={
          new Common.PiccoloError({
            type: 'ParticipantDocumentNotFound',
            message: 'Participant document not found',
          })
        }
      />
    );
  }

  const isEditable =
    participantDocument.status === Common.ParticipantDocumentStatus.Draft && !isDisabled;

  return (
    <ParticipantDocumentEditorContent
      participantDocument={participantDocument}
      isEditable={isEditable}
    />
  );
};
